<template>
  <AuthenticatedLayout activeMenuItem='report-historical-portfolio' title='Reports'>
    <LoadingLayout :isLoading='!pageReady'>
      <PaddedLayout>
        <div class='flex flex-wrap flex-col sm:flex-row py-2 justify-between sm:items-center gap-2 w-full max-w-[80rem]'>
          <h1 class='text-2xl'>Historical Portfolio Report</h1>
          <a :href='pdfUrl' target='_blank' type="submit" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600">Export PDF</a>
        </div>
        <div class='mt-2'>
          <SidebarLayout ref='sidebar'>
            <template #title>
              <h2 class='text-xl'>Filters</h2>
            </template>
            <template #main>
              <form @submit.prevent='applyFilter'>
                <div class="divide-y divide-gray-200 border-b">
                  <div class="space-y-6 pb-5">
                    <MultipleRelationInput label='Theme' collection='theme' v-model='filters.theme' />
                    <MultipleRelationInput label='Objective' collection='objective' v-model='filters.objective' :filters='objectiveFilter' />
                    <MultipleRelationInput label='Assessment Template' collection='assessment-template' v-model='filters.assessmentTemplate' />
                    <MultipleRelationInput label='Asset' collection='organisation-asset' v-model='filters.organisationAsset' v-if='isOrganisationView()' />
                    <MultipleRelationInput label='Asset Manager' collection='organisation-asset-manager' v-model='filters.organisationAssetManager' v-if='isOrganisationView()' />
                    <MultipleRelationInput label='Asset' collection='platform-asset' v-model='filters.organisationAsset' v-if='isSuperAdminView()' />
                    <MultipleRelationInput label='Asset Manager' collection='platform-asset-manager' v-model='filters.organisationAssetManager' v-if='isSuperAdminView()' />
                    <MultipleRelationInput label='Organisation' collection='organisation' v-model='filters.organisation' v-if='isSuperAdminView()' />
                    <YearRangeInput label='Year' v-model='filters.year' :required='true' />
                    <SelectInput label='Resolution' :options='resolutionInputOptions' v-model='filters.resolution' />
                  </div>
                </div>
                <div class='mt-4'>
                  <button type="submit" class="rounded-md bg-jananavy-900 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-jananavy-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-jananavy-600">Filter</button>
                </div>
              </form>
            </template>
          </SidebarLayout>

          <ul class='flex flex-wrap gap-2 items-center'>
            <li class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Resolution: {{ state.filters.resolution === 'year' ? 'Yearly' : 'Half Yearly' }}
            </li>
            <li class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Years: {{ state.filters.year.from }} – {{ state.filters.year.to }}
            </li>
            <li v-if='state.filters.theme?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Themes: <template v-for='theme in state.filters.theme'>{{ theme.name }}</template>
            </li>
            <li v-if='state.filters.objective?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Objectives: <template v-for='objective in state.filters.objective'>{{ objective.name }}</template>
            </li>
            <li v-if='state.filters.assessmentTemplate?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Templates: <template v-for='assessmentTemplate in state.filters.assessmentTemplate'>{{ assessmentTemplate.name }}</template>
            </li>
            <li v-if='state.filters.organisationAsset?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Assets: <template v-for='organisationAsset in state.filters.organisationAsset'>{{ organisationAsset.name }}</template>
            </li>
            <li v-if='state.filters.organisationAssetManager?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Managers: <template v-for='organisationAssetManager in state.filters.organisationAssetManager'>{{ organisationAssetManager.name }}</template>
            </li>
            <li v-if='state.filters.platformAsset?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Assets: <template v-for='platformAsset in state.filters.platformAsset'>{{ platformAsset.name }}</template>
            </li>
            <li v-if='state.filters.platformAssetManager?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Managers: <template v-for='platformAssetManager in state.filters.platformAssetManager'>{{ platformAssetManager.name }}</template>
            </li>
            <li v-if='state.filters.organisation?.length' class='rounded-md p-2 bg-janaturq-100 text-xs'>
              Organisation: <template v-for='organisation in state.filters.organisation'>{{ organisation.name }}</template>
            </li>
            <li class='flex items-center'>
              <button @click='openSidebar' class='rounded-md p-2 border text-xs flex gap-1 items-center'>
                Filter
                <FunnelIcon class='w-4 h-4' aria-hidden='true' />
              </button>
            </li>
          </ul>

          <TabView :tabs='tabs' v-model='state.tab' class='mt-4' />

          <div v-if='state.tab === "results"' class='mt-8'>
            <GraphHistoricalPortfolioTable
              :params='params'
              :showOrganisation='isSuperAdminView()'
              :showAsset='true'
              :showAssetManager='true'
              :view='view'
            >
              <template #title>Results</template>
              <template #description>Scores for all engagements.</template>
            </GraphHistoricalPortfolioTable>
          </div>

          <div v-if='state.tab === "themes"' class='mt-8'>
            <GraphHistoricalThemeBar :params='params' :showKey='true'>
              <template #title>Theme Trends</template>
              <template #description>Theme transformation year on year.</template>
            </GraphHistoricalThemeBar>
          </div>

        </div>
      </PaddedLayout>
    </LoadingLayout>
  </AuthenticatedLayout>
</template>

<script setup>

import { computed, reactive, watch, ref, onActivated } from 'vue';

import { usePayloadRequest } from '@/kernel/api';
import { appendStateToRoute, getStateFromRoute } from '@/router';
import { dehydrateCollections, rehydrateCollections, getReportPDFExportUrl } from '@/kernel/report';

import AuthenticatedLayout from '@/components/layouts/authenticated-layout.vue';
import PaddedLayout from '@/components/layouts/padded-layout.vue';
import LoadingLayout from '@/components/layouts/loading-layout.vue';
import SidebarLayout from '@/components/layouts/sidebar-layout.vue';

import TabView from '@/components/views/tab-view.vue';

import ErrorNotice from '@/components/notices/error-notice.vue';
import WarningNotice from '@/components/notices/warning-notice.vue';

import GraphHistoricalPortfolioTable from '@/components/reports/graphs/graph-historical-portfolio-table.vue';
import GraphHistoricalThemeBar from '@/components/reports/graphs/graph-historical-theme-bar.vue';

import SelectInput from '@/components/inputs/select-input.vue';
import MultipleRelationInput from '@/components/inputs/multiple-relation-input.vue';
import YearRangeInput from '@/components/inputs/year-range-input.vue';

import { FunnelIcon } from '@heroicons/vue/24/outline';

import { getOrganisationId, isSuperAdminView, isOrganisationView } from '@/kernel/session';

import { MultipleRelationInputAdapter } from '@/kernel/adapters';

const tabs = [
  { name: 'Results', key: 'results' },
  { name: 'Themes', key: 'themes' }
];

const view = computed(() => {
  return isSuperAdminView() ? 'platform' : 'organisation';
});

const objectiveFilter = {
  isPublished: {
    equals: true
  },
  isDeprecated: {
    not_equals: true
  }
}

const resolutionInputOptions = [
  {label: 'Year', value: 'year'},
  {label: 'Half Year', value: 'halfYear'}
];

const sidebar = ref();

const openSidebar = () => {
  sidebar.value.open();
}

const closeSidebar = () => {
  sidebar.value.close();
}

const applyFilter = () => {
  Object.assign(state.filters, filters);
  closeSidebar();
}

const filterCollectionMap = [
  {key: 'theme', collection: 'theme'},
  {key: 'objective', collection: 'objective'},
  {key: 'assessmentTemplate', collection: 'assessment-template'},
  {key: 'organisationAsset', collection: 'organisation-asset'},
  {key: 'organisationAssetManager', collection: 'organisation-asset-manager'},
  {key: 'platformAsset', collection: 'platform-asset'},
  {key: 'platformAssetManager', collection: 'platform-asset-manager'},
  {key: 'organisation', collection: 'organisation'}
];

const getDefaultState = (preload) => {
  return {
    tab: preload?.tab || 'results',
    filters: {
      theme: preload?.filters?.theme || null,
      objective: preload?.filters?.objective || null,
      assessmentTemplate: preload?.filters?.assessmentTemplate || null,
      organisationAsset: preload?.filters?.organisationAsset || null,
      organisationAssetManager: preload?.filters?.organisationAssetManager || null,
      platformAsset: preload?.filters?.platformAsset || null,
      platformAssetManager: preload?.filters?.platformAssetManager || null,
      organisation: preload?.filters?.organisation || null,
      year: {
        from: preload?.filters?.year?.from || new Date().getFullYear() - 3,
        to: preload?.filters?.year?.to || new Date().getFullYear()
      },
      resolution: preload?.filters?.resolution || 'year'
    }
  }
}

const state = reactive(getDefaultState());
const filters = reactive(getDefaultState().filters);

const pageReady = ref(false);
const routeState = getStateFromRoute();

const pdfUrl = ref(getReportPDFExportUrl());

const params = computed(() => {
  const args = {...state.filters};

  filterCollectionMap.forEach((pair) => {
    args[pair.key] = MultipleRelationInputAdapter(pair.collection)(pair.key, state.filters[pair.key]);
  });

  if (state.filters.objective) {
    args.objectiveCode = state.filters.objective.map(objective => objective.code);
    delete args.objective;
  }

  if (isOrganisationView()) {
    args.organisation = [getOrganisationId()];
  }

  return args;
});

const stateDidChange = () => {
  const collectionFilters = dehydrateCollections(filterCollectionMap, state.filters);

  appendStateToRoute({
    ...state,
    filters: {
      ...state.filters,
      ...collectionFilters
    }
  });

  setTimeout(() => {
    pdfUrl.value = getReportPDFExportUrl();
  }, 100);
}

const loadState = async () => {
  if (routeState?.filters) {
    const defaultState = getDefaultState(routeState);

    const collectionFilters = await rehydrateCollections(filterCollectionMap, defaultState.filters);

    defaultState.filters = {
      ...defaultState.filters,
      ...collectionFilters
    }

    Object.assign(state, defaultState);
    Object.assign(filters, defaultState.filters);
  }

  pageReady.value = true;

  watch(state, () => {
    stateDidChange();
  });
}

onActivated(() => {
  if (pageReady.value) {
    stateDidChange();
  } else {
    loadState();
  }
});

</script>
